import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { useTranslations } from 'use-intl';
import { useLanguage } from 'i18n';
import { Button, Message } from 'components';
import { FirebaseContext } from 'context';
import { AnimatePresence } from 'motion/react';

function MagicLinkLoginForm({ sessionId, onSuccess }) {
  const t = useTranslations('Auth');
  const { language } = useLanguage();
  const { firebase } = useContext(FirebaseContext);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [focused, setFocused] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setMessage('');

    try {
      // Store email in localStorage for later use during sign in
      window.localStorage.setItem(`emailForSignIn-${sessionId}`, email);

      // Call Firebase function to send magic link
      await firebase.emails.sendMagicLink({
        email: email.toLowerCase().trim(),
        sessionId,
        origin: window.location.origin,
        href: window.location.href,
        // Sender identity and brand name resolve server-side (config/tenant).
        emailData: {},
        language
      });

      setMessage(t('magicLinkSent', { email }));
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Magic link error:', err);
      setErrorMessage(err.message || t('magicLinkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h2>{t('magicLinkTitle')}</h2>
      <p>{t('magicLinkIntro')}</p>
      <form onSubmit={handleSubmit}>
        <InputWrapper>
          <StyledInput
            id="magiclink-email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            autoComplete="email"
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          <StyledLabel htmlFor="magiclink-email" $float={focused || !!email}>
            {t('emailLabel')}
          </StyledLabel>
        </InputWrapper>
        <Button
          type="submit"
          loading={loading}
          disabled={!email || loading}
          changeColorWhenDisabled={false}
          shine={!loading}
          style={{ marginTop: '30px' }}>
          {t('sendSecureLink')}
        </Button>
      </form>
      <AnimatePresence>
        {message && <Message type="success" message={message} />}
      </AnimatePresence>
      <AnimatePresence>
        {errorMessage && <Message type="error" message={errorMessage} />}
      </AnimatePresence>
    </Container>
  );
}

const Container = styled.section`
  background-color: #fff;
  border-radius: var(--border-radius-md);
  margin: 0 auto;
  max-width: 28.375rem;
  padding: 3.25rem 1.875rem 2.5rem;
  perspective: 50rem;
  position: relative;
  width: 100%;
  z-index: 1;

  @media (min-width: 1200px) {
    max-width: 28.375rem;
    padding: 3.25rem 1.875rem 2.5rem;
  }

  h2,
  p {
    text-align: center;
  }

  p {
    margin-bottom: 2.125rem;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1em;
    text-transform: uppercase;
  }

  form {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
  }

  label,
  input {
    width: 100%;
  }

  label {
    /* margin-bottom: 1.188rem; */
    width: fit-content;

    &:last-of-type {
      margin-bottom: 0.5rem;
    }
  }
`;

// Floating label animation styles
const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  /* margin-bottom: 1.188rem; */
`;

const StyledInput = styled.input`
  background: #fff;
  border: 1px solid var(--light-grey);
  border-radius: 4px;
  font-size: 1rem;
  padding: 1rem 0.75rem;
  transition: border-color 0.2s;
  width: 100%;

  &:focus {
    border-color: var(--blue);
    outline: none;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const StyledLabel = styled.label`
  background: #fff;
  color: var(--light-brown);
  font-size: 1rem;
  left: 1.1rem;
  padding: 0 0.25rem;
  pointer-events: none;
  position: absolute;
  top: 1.15rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: fit-content;
  z-index: 2;

  ${({ $float }) =>
    $float &&
    `
      top: -1.25rem;
      left: 0rem;
      font-size: 0.78rem;
      color: var(--blue);
      background: #fff;
      padding: 0 0.25rem;
      z-index: 2;
    `}
`;

export default MagicLinkLoginForm;
